/**
 * Lab Queue Page - Clara Vale V2
 *
 * Decision-first design: Lab techs see what needs attention at a glance,
 * grouped by defect type so internal assignment is fast. Matches the
 * Registrations/Claims list page structure exactly.
 */

import { useCallback, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { User, ChevronRight, Download } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { AppShell } from '@/admin/components/app-shell'
import {
  DataTable,
  type DataTableColumn,
  ColumnToggle,
} from '@/admin/components/data-table'
import { StatusPills, type StatusPillItem } from '@/admin/components/shared/status-pills'
import { ListPageHeader } from '@/admin/components/shared/list-page-header'
import type { TimeRange } from '@/admin/components/shared/time-filter'
import { useLabQueue, useAssignLabTask, type LabQueueItem } from '@/admin/hooks/use-lab-tasks'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import { timeRangeToParams } from '@/admin/lib/time-range-params'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { ProductFilterMenu } from '@/shared/components/ui/product-filter-menu'
import { formatClaimId } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'

const TABLE_ID = 'lab-queue'

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  REQUESTED: { label: 'Unassigned', variant: 'outline' },
  ASSIGNED: { label: 'Assigned', variant: 'secondary' },
  IN_PROGRESS: { label: 'In Progress', variant: 'default' },
  COMPLETE: { label: 'Complete', variant: 'secondary' },
  CANCELLED: { label: 'Cancelled', variant: 'destructive' },
}

const PRIORITY_COLORS: Record<string, string> = {
  HIGH: 'text-[var(--red)]',
  STANDARD: 'text-[var(--ink-2)]',
  LOW: 'text-[var(--ink-3)]',
}

export function LabQueuePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>()

  // The platform's standard page: search, the Filters panel, the status pills
  // and sorting all run on the server, so every claim in Lab is reachable.
  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID)
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID)
  const debouncedSearch = useDebouncedValue(search)
  const params = useMemo(() => {
    const p: Record<string, string> = {
      ...queryParams,
      ...timeRangeToParams(timeRange, customRange),
    }
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim()
    if (statusFilter) p.status = statusFilter
    // Filters panel: each facet's values comma-joined (the server ORs them).
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(',')
    }
    return p
  }, [queryParams, timeRange, customRange, debouncedSearch, statusFilter, filterValues])
  const { data, isLoading, isFetching, isError, refetch } = useLabQueue(params)
  const queue = data?.results ?? []

  const counts = data?.status_counts ?? {}
  const queueTotal = Object.values(counts).reduce((n, c) => n + c, 0)
  const statusPills: StatusPillItem[] = []
  if (counts['REQUESTED']) statusPills.push({ key: 'REQUESTED', label: 'Unassigned', count: counts['REQUESTED'] })
  if (counts['ASSIGNED']) statusPills.push({ key: 'ASSIGNED', label: 'Assigned', count: counts['ASSIGNED'] })
  if (counts['IN_PROGRESS']) statusPills.push({ key: 'IN_PROGRESS', label: 'In Progress', count: counts['IN_PROGRESS'] })
  if (counts['COMPLETE']) statusPills.push({ key: 'COMPLETE', label: 'Complete', count: counts['COMPLETE'] })

  const unassignedCount = data?.unassigned_count ?? 0

  const handleTimeFilterChange = useCallback(
    (range: TimeRange, custom?: { from: string; to: string }) => {
      setTimeRange(range)
      setCustomRange(custom)
      setPage(1)
    },
    [setPage],
  )

  const columns: DataTableColumn<LabQueueItem>[] = [
    {
      id: 'claim_ref',
      header: 'Claim',
      cell: (item) => (
        <div>
          <p className="text-[12px] font-medium text-[var(--ink)]">
            {item.claim_ref || formatClaimId(item.claim_uuid)}
          </p>
          {item.product_category && (
            <p className="text-[10px] text-[var(--ink-3)]">{item.product_category}</p>
          )}
        </div>
      ),
    },
    {
      // Right after the claim, so it is in view without scrolling, as on
      // every list. The claim's own updated_at, which moves whenever it is
      // worked.
      id: 'updated_at',
      header: t('common.lastActivity', 'Last Activity'),
      sortable: true,
      sortField: 'updated_at',
      sortKind: 'date',
      cell: (item) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {item.updated_at ? formatDate(item.updated_at) : '-'}
        </span>
      ),
    },
    {
      id: 'defect_type',
      header: 'Defect Type',
      cell: (item) => (
        <span className="text-[12px] text-[var(--ink-2)]">
          {item.defect_type
            ? item.defect_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
            : '-'}
        </span>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (item) => (
        <span className={cn('text-[12px] font-medium', PRIORITY_COLORS[item.priority ?? 'STANDARD'])}>
          {item.priority ?? 'Standard'}
        </span>
      ),
    },
    {
      id: 'lab_task_status',
      header: 'Status',
      cell: (item) => {
        const status = item.lab_task_status ?? 'REQUESTED'
        const badge = STATUS_BADGES[status] ?? STATUS_BADGES.REQUESTED
        return <Badge variant={badge.variant}>{badge.label}</Badge>
      },
    },
    {
      id: 'assigned_to_name',
      header: 'Technician',
      cell: (item) =>
        item.assigned_to_name ? (
          <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
            <User className="h-3 w-3" />
            {item.assigned_to_name}
          </span>
        ) : (
          <AssignToMeButton
            claimUuid={item.claim_uuid}
            taskUuid={item.lab_task_uuid}
          />
        ),
    },
    {
      id: 'days_in_lab',
      header: 'Days in Lab',
      cell: (item) => (
        <span className="text-[12px] tabular-nums text-[var(--ink-3)]">
          {item.days_in_lab ?? 0}d
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: () => (
        <ChevronRight className="h-3.5 w-3.5 text-[var(--ink-3)]" />
      ),
    },
  ]

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* ── Page Header (clara-title + subtitle) ─────────── */}
        <ListPageHeader
          title="Lab"
          totalCount={queueTotal}
          highlightStat={
            unassignedCount > 0
              ? { label: 'awaiting assignment', value: unassignedCount }
              : undefined
          }
          timeFilterValue={timeRange}
          timeFilterCustomRange={customRange}
          onTimeFilterChange={handleTimeFilterChange}
        />

        {/* ── Toolbar: Status pills | Search + Columns + Export ── */}
        <div className="cv-data-table relative z-10 mb-5 flex flex-wrap items-center justify-between gap-2 md:gap-3">
          {/* Left: Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <StatusPills
              items={statusPills}
              activeKey={statusFilter}
              onSelect={(key) => {
                setStatusFilter(key)
                setPage(1)
              }}
              totalCount={queueTotal}
            />
          </div>

          {/* Right: Search + Columns + Export */}
          <div className="flex items-center gap-2">
            <ColumnToggle
              columns={columns}
              visibility={columnVisibility}
              onVisibilityChange={setColumnVisibility}
            />
            <ProductFilterMenu
              facets={data?.filter_options ?? []}
              value={filterValues}
              onChange={(values) => {
                setFilterValues(values)
                setPage(1)
              }}
            />
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value)
                setPage(1)
              }}
              placeholder="Search claims..."
              className="w-[200px]"
            />
            <Button
              variant="outline"
              size="icon-sm"
              className="shrink-0"
              disabled
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* ── Queue Table ──────────────────────────────────── */}
        <DataTable
          tableId={TABLE_ID}
          columns={columns}
          data={queue}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={refetch}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          columnVisibility={columnVisibility}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next)
            setPage(1)
          }}
          rowKey={(item) => item.claim_uuid}
          onRowClick={(item) => navigate(`/claims/${item.claim_uuid}`)}
          emptyMessage={
            isLoading
              ? 'Loading lab queue…'
              : search
                ? 'No claims match your search'
                : statusFilter
                  ? `No ${STATUS_BADGES[statusFilter]?.label.toLowerCase() ?? ''} claims`
                  : 'No claims in the lab queue'
          }
        />
      </main>
    </AppShell>
  )
}

function AssignToMeButton({
  claimUuid,
  taskUuid,
}: {
  claimUuid: string
  taskUuid: string | null
}) {
  const assign = useAssignLabTask(claimUuid, taskUuid ?? '')

  if (!taskUuid) return <span className="text-[11px] text-[var(--ink-3)]">No task</span>

  return (
    <Button
      variant="outline"
      size="xs"
      disabled={assign.isPending}
      onClick={(e) => {
        e.stopPropagation()
        assign.mutate()
      }}
      className="text-[10px] uppercase tracking-[0.08em]"
    >
      {assign.isPending ? 'Assigning…' : 'Assign to Me'}
    </Button>
  )
}
